import { useEffect, useRef } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useRouter, useRouterState } from "@tanstack/react-router";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/lib/supabase";
import { acquireSharedRealtimeChannel } from "@/lib/sharedRealtimeChannel";
import {
  fetchSquadById,
  onSquadDissolved,
  onSquadLeft,
  resolveDissolvedSquadName,
  resolveSquadJoinCode,
  squadNightsForSquadKey,
  squadPageContextKey,
  type Squad,
  type SquadNight,
  type SquadPageContext,
} from "@/lib/squad";
import {
  isDissolvingSquad,
  isPendingInviteDecline,
  isPendingSelfLeave,
  markDissolvingSquad,
} from "@/lib/squadLeaveSync";
import i18n from "@/lib/i18next";

const DEDUPE_MS = 2_000;
const MEMBER_REMOVED_DEBOUNCE_MS = 400;
/** After a null fetch, wait for squads DELETE before treating as admin-kick (RLS hides squads from ex-members). */
const DISSOLVE_RACE_WAIT_MS = 600;

function collectCachedNightIds(queryClient: QueryClient, squadId: string): string[] {
  // Best-effort only: after membership loss, squad_nights RLS may block refetch.
  const nights = queryClient.getQueryData<SquadNight[]>(squadNightsForSquadKey(squadId));
  return nights?.map((n) => n.id) ?? [];
}

function isViewingSquad(
  queryClient: QueryClient,
  pathname: string,
  squadId: string,
): boolean {
  const match = pathname.match(/^\/squad\/([^/?#]+)/);
  if (!match) return false;

  const captured = match[1];
  if (!captured) return false;

  const urlId = decodeURIComponent(captured);
  if (urlId === squadId) return true;

  const ctx = queryClient.getQueryData<SquadPageContext>(squadPageContextKey(urlId));
  if (ctx?.persistentSquad?.id === squadId) return true;

  const nights = queryClient.getQueryData<SquadNight[]>(squadNightsForSquadKey(squadId));
  return nights?.some((n) => n.id === urlId) ?? false;
}

export function useSquadMembershipSync() {
  const { user, isGuest, hydrated } = useAuth();
  const queryClient = useQueryClient();
  const router = useRouter();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;

  const handledAtRef = useRef<Map<string, number>>(new Map());
  const memberRemovedTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const userId = user && !isGuest ? user.id : undefined;

  useEffect(() => {
    if (!hydrated || !userId) return;

    const shouldHandle = (squadId: string): boolean => {
      const now = Date.now();
      const last = handledAtRef.current.get(squadId);
      if (last != null && now - last < DEDUPE_MS) return false;
      handledAtRef.current.set(squadId, now);
      return true;
    };

    const redirectIfViewing = (squadId: string) => {
      if (isViewingSquad(queryClient, pathnameRef.current, squadId)) {
        router.navigate({ to: "/mynight" });
      }
    };

    const handleDissolved = async (squadId: string) => {
      if (!shouldHandle(squadId)) return;

      const nightIds = collectCachedNightIds(queryClient, squadId);
      await onSquadDissolved(queryClient, {
        squadId,
        nightIds,
        proposalIds: [],
        userId,
      });

      if (isViewingSquad(queryClient, pathnameRef.current, squadId)) {
        toast.info(i18n.t("consumer:squad.dissolvedToast"));
        redirectIfViewing(squadId);
      }
    };

    const handleKicked = async (squadId: string, fetchedSquad: Squad | null) => {
      if (!shouldHandle(squadId)) return;

      const squadName =
        fetchedSquad?.name ?? resolveDissolvedSquadName(queryClient, squadId, userId);
      const joinCode =
        fetchedSquad?.join_code ?? (await resolveSquadJoinCode(queryClient, squadId));

      // nightIds: cache-only after RLS blocks squad_nights for ex-members.
      const nightIds = collectCachedNightIds(queryClient, squadId);

      await onSquadLeft(queryClient, {
        squadId,
        userId,
        squadName,
        joinCode,
        reason: "kicked",
        nightIds,
        proposalIds: [],
      });

      toast.info(i18n.t("consumer:squad.kickedToast"));
      redirectIfViewing(squadId);
    };

    const runMemberRemovedCheck = async (squadId: string) => {
      if (isPendingSelfLeave(squadId)) return;
      if (isPendingInviteDecline(squadId)) return;
      if (isDissolvingSquad(squadId)) return;

      let fetchedSquad: Squad | null = null;
      try {
        fetchedSquad = await fetchSquadById(squadId);
      } catch {
        fetchedSquad = null;
      }

      if (fetchedSquad) {
        await handleKicked(squadId, fetchedSquad);
        return;
      }

      // Squad row gone, or RLS hides it from ex-members — wait for squads DELETE signal.
      await new Promise((resolve) => setTimeout(resolve, DISSOLVE_RACE_WAIT_MS));

      if (isPendingSelfLeave(squadId)) return;
      if (isPendingInviteDecline(squadId)) return;
      if (isDissolvingSquad(squadId)) {
        await handleDissolved(squadId);
        return;
      }

      // No dissolve signal: admin-kick (fetch null due to squad_select RLS for ex-members).
      await handleKicked(squadId, null);
    };

    const scheduleMemberRemovedCheck = (squadId: string) => {
      const existing = memberRemovedTimersRef.current.get(squadId);
      if (existing) clearTimeout(existing);

      const timer = setTimeout(() => {
        memberRemovedTimersRef.current.delete(squadId);
        void runMemberRemovedCheck(squadId);
      }, MEMBER_REMOVED_DEBOUNCE_MS);

      memberRemovedTimersRef.current.set(squadId, timer);
    };

    const topic = `squad-membership-sync-${userId}`;
    const release = acquireSharedRealtimeChannel(
      topic,
      (broadcast) =>
        supabase
          .channel(topic)
          .on(
            "postgres_changes",
            { event: "DELETE", schema: "public", table: "squads" },
            (payload) => {
              const squadId = (payload.old as { id?: string }).id;
              if (!squadId) return;
              markDissolvingSquad(squadId);
              void handleDissolved(squadId);
              broadcast();
            },
          )
          .on(
            "postgres_changes",
            {
              // Preferred kick/leave signal: INSERT is filterable; DELETE on
              // squad_members is not (Realtime limitation).
              event: "INSERT",
              schema: "public",
              table: "squad_removal_log",
              filter: `user_id=eq.${userId}`,
            },
            (payload) => {
              const row = payload.new as { squad_id?: string; reason?: string };
              const squadId = row.squad_id;
              if (!squadId) return;

              if (isPendingSelfLeave(squadId)) return;
              if (isPendingInviteDecline(squadId)) return;
              if (isDissolvingSquad(squadId)) return;

              scheduleMemberRemovedCheck(squadId);
              broadcast();
            },
          )
                          .on(
                            "postgres_changes",
                            {
                              // Fallback when replica identity exposes user_id on DELETE.
                              // Prefer squad_removal_log INSERT above (filterable).
                              event: "DELETE",
                              schema: "public",
                              table: "squad_members",
                            },
                            (payload) => {
                              const old = payload.old as {
                                squad_id?: string;
                                user_id?: string;
                              };
                              if (old.user_id !== userId) return;

                              const squadId = old.squad_id;
                              if (!squadId) return;

                              if (isPendingSelfLeave(squadId)) return;
                              if (isPendingInviteDecline(squadId)) return;
                              if (isDissolvingSquad(squadId)) return;

                              scheduleMemberRemovedCheck(squadId);
                              broadcast();
                            },
                          )
          .subscribe(),
      () => {
        /* Primary work runs in channel handlers; secondary mounts are no-ops. */
      },
    );

    return () => {
      for (const timer of memberRemovedTimersRef.current.values()) {
        clearTimeout(timer);
      }
      memberRemovedTimersRef.current.clear();
      release();
    };
  }, [hydrated, userId, queryClient, router]);
}
